import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppSurfaceService } from '@core/config/app-surface.service';

/**
 * The EVO mark and wordmark in the app bar.
 *
 * Ported from evo-accounts `core/components/brand/brand.component.ts`. The mark
 * is inline SVG filled with `currentColor` rather than an <img>, which is the
 * entire reason it works in both themes: the colour comes from
 * `--c-brand-logo` (the EVO blue in light, plain white in dark) and there is no
 * second asset to ship, no per-theme `src` swap, and nothing to go missing.
 *
 * Two things differ from evo-accounts, and only two:
 *
 *  - the wordmark names the product this host serves -- "Briefings" on the
 *    briefings host, "Estimator" on the estimator one -- rather than
 *    "Accounts";
 *  - the link is a `routerLink`, not an `href`. evo-accounts points its mark at
 *    `/`, which is a different application; here the mark goes to `/v2`, which
 *    is a route in this one, and a bare href would reload the app to get there.
 *
 * The wordmark is derived from the surface rather than passed in by each
 * caller, so the two consumers cannot disagree about which product they are
 * sitting in. The accessible name is derived from the same value: a mark that
 * reads "Briefings" and announces "Estimator" is worse than either alone.
 *
 * The sign-in page is deliberately not a special case. It is only ever served
 * from the estimator origin -- `ZITADEL_REDIRECT_URI` points there and does not
 * move -- so it says "Estimator" because that is where the visitor is, even
 * when they arrived from a briefing.
 */
@Component({
  selector: 'app-v2-brand',
  standalone: true,
  imports: [NgTemplateOutlet, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (route(); as link) {
      <a class="brand" [routerLink]="link" [attr.aria-label]="'EVO ' + productName + ' home'">
        <ng-container [ngTemplateOutlet]="mark" />
      </a>
    } @else {
      <span class="brand">
        <ng-container [ngTemplateOutlet]="mark" />
      </span>
    }

    <ng-template #mark>
      <svg class="brand-logo" viewBox="0 0 338.2 82.6" role="img" aria-label="EVO">
        <polygon
          fill="currentColor"
          points="210.8,76.7 179.1,0.5 178.8,0 174.5,0.1 208.8,82.6 212.6,82.6 246.1,0.1 241.7,0"
        />
        <path
          fill="currentColor"
          d="M296.9,0c-22.8,0-41.3,18.5-41.3,41.3c0,22.8,18.5,41.3,41.3,41.3c22.8,0,41.3-18.5,41.3-41.3C338.2,18.5,319.7,0,296.9,0z M334.2,41.3c0,20.6-16.7,37.3-37.3,37.3c-20.6,0-37.3-16.7-37.3-37.3S276.4,4,296.9,4C317.5,4,334.2,20.7,334.2,41.3z"
        />
        <path
          fill="currentColor"
          d="M165.4,41.5l0-0.5c0-0.3,0-0.6-0.1-0.9c-0.7-22.2-18.9-40-41.2-40c-22,0-40,17.3-41.2,39L0,41.2l82.9,1.7c0.8,22,19,39.7,41.2,39.7c7.9,0,15.7-2.3,22.3-6.6l0.9-0.6l-2.7-3l-0.6,0.4c-6,3.8-12.8,5.8-19.9,5.8c-20,0-36.4-15.9-37.2-35.7h78.5l0-0.8h0L165.4,41.5z M161.3,39H87c1.2-19.5,17.4-35,37.2-35C143.9,4,160.1,19.5,161.3,39z"
        />
      </svg>
      <span class="brand-name">{{ productName }}</span>
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: flex;
        min-width: 0;
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        text-decoration: none;
      }

      /* Size and colour are read from custom properties so a host that is not
         the app bar — a sign-in page painting its own navy field — can retune
         the mark by setting two variables on <app-v2-brand>, rather than by
         shipping a second copy of the artwork. */
      .brand-logo {
        height: var(--brand-mark-size, 14px);
        width: auto;
        flex: none;
        display: block;
        color: var(--brand-mark-color, var(--c-brand-logo));
      }

      /* On a phone the mark stands alone. It is recognisable on its own, and
         the bar's width there is better spent on the account controls than on
         a word. */
      .brand-name {
        display: var(--brand-name-display, none);
        font-family: var(--f-display);
        font-weight: 700;
        font-size: var(--brand-name-size, 15px);
        letter-spacing: 0.01em;
        color: var(--brand-name-color, var(--c-ink));
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      @media (min-width: 576px) {
        .brand-name {
          display: var(--brand-name-display, block);
        }
      }
    `,
  ],
})
export class V2BrandComponent {
  private readonly surface = inject(AppSurfaceService);

  /** A link when the bar is interactive, plain text when it is not. */
  readonly route = input<string | null>(null);

  /**
   * The product this host serves, in the wordmark and in the link's name.
   *
   * A plain property, not a `computed`: the surface is decided by the hostname
   * and cannot change while the page is open, so there is nothing to react to.
   */
  protected readonly productName =
    this.surface.current === 'briefings' ? 'Briefings' : 'Estimator';
}
